import React, { useEffect, useState } from "react";
import {
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import useTabTitle from "../hooks/useTabTitle.hook";
import useDetectSession from "../hooks/useDetectSession.hook";
import AppContainer from "../components/AppContainer";
import NotificationFileList from "./NotificationFileListPage";
import UploadBatchNotifications from "./UploadBatchNotificationsPage";
import UploadNotification from "./UploadNotificationPage";
import { RootState } from "../redux/store";
import { useSelector } from "react-redux";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";

type View = "list" | "uploadBatch" | "uploadSingle";

const NotificationForm: React.FC = () => {
  useTabTitle();

  const [selectedView, setSelectedView] = useState<View>("list");

  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  useDetectSession();

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  const renderContent = () => {
    switch (selectedView) {
      case "uploadBatch":
        return <UploadBatchNotifications />;
      case "uploadSingle":
        return <UploadNotification />;
      case "list":
      default:
        return <NotificationFileList />;
    }
  };

  return (
    <AppContainer>
      <Grid container sx={{ height: "100vh", overflow: "hidden" }}>
        {/* Sidebar */}
        <Grid container spacing={4} sx={{ padding: 4 }} position={"absolute"}>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ padding: 2 }}>
              <Typography variant="h4" gutterBottom>
                Notifications
              </Typography>

              <List>
                {currentUserPermissions?.notification?.permissions.view && (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedView === "list"}
                      onClick={() => setSelectedView("list")}
                      sx={{
                        px: 2,
                        py: 1.5,
                        mb: 1,
                        borderRadius: 30,
                        cursor: "pointer",
                        backgroundColor:
                          selectedView === "list" ? "#eef4fc" : "grey.100",
                        color: "text.primary",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor:
                            selectedView === "list" ? "#eef4fc" : "grey.200",
                        },
                      }}
                    >
                      <ListItemText primary="Notification File List" />
                    </ListItemButton>
                  </ListItem>
                )}
                {currentUserPermissions?.notification?.permissions.edit && (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedView === "uploadBatch"}
                      onClick={() => setSelectedView("uploadBatch")}
                      sx={{
                        px: 2,
                        py: 1.5,
                        mb: 1,
                        borderRadius: 30,
                        cursor: "pointer",
                        backgroundColor:
                          selectedView === "uploadBatch"
                            ? "#eef4fc"
                            : "grey.100",
                        color: "text.primary",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor:
                            selectedView === "uploadBatch"
                              ? "#eef4fc"
                              : "grey.200",
                        },
                      }}
                    >
                      <ListItemText primary="Upload Batch Notifications" />
                    </ListItemButton>
                  </ListItem>
                )}
                {currentUserPermissions?.notification?.permissions.edit && (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedView === "uploadSingle"}
                      onClick={() => setSelectedView("uploadSingle")}
                      sx={{
                        px: 2,
                        py: 1.5,
                        mb: 1,
                        borderRadius: 30,
                        cursor: "pointer",
                        backgroundColor:
                          selectedView === "uploadSingle"
                            ? "#eef4fc"
                            : "grey.100",
                        color: "text.primary",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor:
                            selectedView === "uploadSingle"
                              ? "#eef4fc"
                              : "grey.200",
                        },
                      }}
                    >
                      <ListItemText primary="Upload Notification" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid
            item
            xs={12}
            md={9}
            sx={{
              height: "100vh",
              overflowY: "auto",
              padding: 2,
            }}
          >
            {renderContent()}
          </Grid>
        </Grid>
      </Grid>
    </AppContainer>
  );
};

export default NotificationForm;
